import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { fetchGenderReport, fetchYojanaReport, parseGenderReport } from '../utils/api';
import { selectCurrentUser } from '../store/slices/authSlice';
import { formatLocalDateTime } from '../utils/dateTime';

const getTimeOfDayGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const parseYojanaReport = (data) => {
  const arr = Array.isArray(data) ? data : [];
  const byGovt = {};
  arr.forEach(({ yojanaGovernment, totalCount }) => {
    byGovt[yojanaGovernment] = totalCount;
  });
  return {
    totalYojana: byGovt.totalYojana ?? 0,
    central: byGovt.Central ?? 0,
    state: byGovt.State ?? 0,
    local: byGovt.Local ?? 0
  };
};

const useCountUp = (value, duration = 700) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const end = Number(value) || 0;
    const startTime = performance.now();
    let frame;

    const tick = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      setDisplay(Math.round(end * progress));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return display;
};

const hoverPaperSx = {
  p: 2,
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: 4
  }
};

const CountValue = ({ value, color, variant = 'h4' }) => {
  const display = useCountUp(value);
  return (
    <Typography variant={variant} color={color}>
      {display.toLocaleString()}
    </Typography>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  const [dashboardData, setDashboardData] = useState({
    users: {
      total: 0,
      male: 0,
      female: 0
    },
    yojana: {
      totalYojana: 0,
      central: 0,
      state: 0,
      local: 0
    }
  });

  const displayName =
    user?.userName ||
    user?.name ||
    user?.firstName ||
    (typeof window !== 'undefined' ? localStorage.getItem('userName') : '') ||
    '';

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadDashboardData = async () => {
      setLoading(true);
      setError(null);
      const errors = [];

      try {
        let usersData = { total: 0, male: 0, female: 0 };
        let yojanaData = { totalYojana: 0, central: 0, state: 0, local: 0 };

        try {
          const genderResponse = await fetchGenderReport();
          usersData = parseGenderReport(genderResponse?.data);
        } catch (err) {
          console.error('Error loading gender report:', err);
          errors.push('App users');
        }

        try {
          const yojanaResponse = await fetchYojanaReport();
          const rawYojana = yojanaResponse?.data;
          const yojanaArray = Array.isArray(rawYojana)
            ? rawYojana
            : (rawYojana?.data || []);
          yojanaData = parseYojanaReport(yojanaArray);
        } catch (err) {
          console.error('Error loading yojana report:', err);
          errors.push('Yojana summary');
        }

        if (cancelled) return;

        setDashboardData({
          users: usersData,
          yojana: yojanaData
        });

        if (errors.length === 2) {
          setError('Failed to load dashboard data. Please try again later.');
        }
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || 'Failed to load dashboard data. Please try again later.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDashboardData();
    return () => {
      cancelled = true;
    };
  }, []);

  const maleShare = useMemo(() => {
    const total = dashboardData.users.total || 0;
    if (!total) return 0;
    return Math.round((dashboardData.users.male / total) * 100);
  }, [dashboardData.users]);

  const genderChartData = useMemo(() => ([
    { name: 'Male', value: dashboardData.users.male || 0 },
    { name: 'Female', value: dashboardData.users.female || 0 }
  ]), [dashboardData.users]);

  const yojanaChartData = useMemo(() => ([
    { name: 'Central', value: dashboardData.yojana.central || 0 },
    { name: 'State', value: dashboardData.yojana.state || 0 },
    { name: 'Local', value: dashboardData.yojana.local || 0 }
  ]), [dashboardData.yojana]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, bgcolor: '#fff9f9', m: 2 }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, m: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Welcome to Sahaj Yojana Administration
      </Typography>
      <Typography variant="h6" color="text.secondary">
        {getTimeOfDayGreeting()}{displayName ? `, ${displayName}` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {formatLocalDateTime(now)}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              boxShadow: 3,
              height: '100%',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: 6 }
            }}
          >
            <CardActionArea onClick={() => navigate('/dashboard/users')} sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PeopleAltOutlinedIcon color="primary" />
                  <Typography variant="h5" component="div">
                    App Users Summary
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper sx={{ ...hoverPaperSx, backgroundColor: '#f5f5f5' }}>
                      <CountValue value={dashboardData.users.total} color="primary" />
                      <Typography variant="body1">Total Registered Users</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper sx={{ ...hoverPaperSx, backgroundColor: '#e8eaf6' }}>
                      <CountValue value={dashboardData.users.male} color="primary" />
                      <Typography variant="body1">Male Users</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper sx={{ ...hoverPaperSx, backgroundColor: '#fce4ec' }}>
                      <CountValue value={dashboardData.users.female} color="secondary" />
                      <Typography variant="body1">Female Users</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Male {maleShare}% · Female {100 - maleShare}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={maleShare}
                      sx={{ mt: 0.5, height: 8, borderRadius: 4 }}
                    />
                  </Grid>
                  {(dashboardData.users.male > 0 || dashboardData.users.female > 0) && (
                    <Grid item xs={12} sx={{ height: 160 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={genderChartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={60}
                          >
                            <Cell fill="#3f51b5" />
                            <Cell fill="#e91e63" />
                          </Pie>
                          <Tooltip formatter={(value) => Number(value).toLocaleString()} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              boxShadow: 3,
              height: '100%',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: 6 }
            }}
          >
            <CardActionArea onClick={() => navigate('/dashboard/schemes')} sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccountBalanceOutlinedIcon color="primary" />
                  <Typography variant="h5" component="div">
                    Yojana Summary
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper sx={{ ...hoverPaperSx, backgroundColor: '#f5f5f5' }}>
                      <CountValue value={dashboardData.yojana.totalYojana} color="primary" />
                      <Typography variant="body1">Total Yojana</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ ...hoverPaperSx, backgroundColor: '#e3f2fd' }}>
                      <CountValue value={dashboardData.yojana.central} color="primary" variant="h5" />
                      <Typography variant="body2">Central Govt</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ ...hoverPaperSx, backgroundColor: '#e0f7fa' }}>
                      <CountValue value={dashboardData.yojana.state} color="secondary" variant="h5" />
                      <Typography variant="body2">State Govt</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ ...hoverPaperSx, backgroundColor: '#f1f8e9' }}>
                      <CountValue value={dashboardData.yojana.local} color="success.main" variant="h5" />
                      <Typography variant="body2">Local Govt</Typography>
                    </Paper>
                  </Grid>
                  {(dashboardData.yojana.central + dashboardData.yojana.state + dashboardData.yojana.local) > 0 && (
                    <Grid item xs={12} sx={{ height: 160 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={yojanaChartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={60}
                          >
                            <Cell fill="#1976d2" />
                            <Cell fill="#00838f" />
                            <Cell fill="#689f38" />
                          </Pie>
                          <Tooltip formatter={(value) => Number(value).toLocaleString()} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HomePage;
